import type { Clock } from './clock'

export interface GameLoopCallbacks {
  /** One fixed physics/game step. dtSeconds is always the same value every call. */
  update(dtSeconds: number): void
  /** Called once per step() invocation, after zero or more update() calls. alpha in [0, 1) is interpolation factor. */
  render(alpha: number): void
}

export interface GameLoopOptions {
  /** Fixed physics timestep in milliseconds. Defaults to 1000/60. */
  fixedTimestepMs?: number
  /** Cap on update() calls per step() to avoid a spiral of death after a long stall. */
  maxSubstepsPerStep?: number
}

const DEFAULT_FIXED_TIMESTEP_MS = 1000 / 60
const DEFAULT_MAX_SUBSTEPS = 5

/**
 * Fixed-timestep accumulator loop, driven by an injectable Clock rather than reading
 * wall-clock time itself. In production this gets a RealClock and is driven by
 * requestAnimationFrame; in test mode it gets the test-mode ManualClock and is driven
 * by explicit tick()+step() calls, so the exact same update()/render() sequence runs
 * either way - "test mode" is purely a matter of which clock feeds it.
 */
export class GameLoop {
  private readonly clock: Clock
  private readonly fixedTimestepMs: number
  private readonly maxSubstepsPerStep: number

  private lastTimeMs: number | null = null
  private accumulatorMs = 0

  constructor(clock: Clock, opts: GameLoopOptions = {}) {
    this.clock = clock
    this.fixedTimestepMs = opts.fixedTimestepMs ?? DEFAULT_FIXED_TIMESTEP_MS
    this.maxSubstepsPerStep = opts.maxSubstepsPerStep ?? DEFAULT_MAX_SUBSTEPS
  }

  /** Elapsed sim time since the loop started, per the injected clock. */
  get simTimeMs(): number {
    return this.lastTimeMs ?? this.clock.now()
  }

  /**
   * Consumes however much time has passed on the clock since the previous call,
   * running update() at a fixed timestep (0+ times, capped by maxSubstepsPerStep),
   * then calling render() exactly once.
   */
  step(callbacks: GameLoopCallbacks): void {
    const now = this.clock.now()
    if (this.lastTimeMs === null) this.lastTimeMs = now

    const elapsedMs = now - this.lastTimeMs
    this.lastTimeMs = now
    this.accumulatorMs += elapsedMs

    let substeps = 0
    while (this.accumulatorMs >= this.fixedTimestepMs && substeps < this.maxSubstepsPerStep) {
      callbacks.update(this.fixedTimestepMs / 1000)
      this.accumulatorMs -= this.fixedTimestepMs
      substeps += 1
    }

    if (this.accumulatorMs >= this.fixedTimestepMs) {
      // If we broke out of the loop because of the maxSubstepsPerStep cap,
      // discard the remaining accumulated time to prevent a permanent spiral of catch-up.
      this.accumulatorMs = this.accumulatorMs % this.fixedTimestepMs
    }

    const alpha = this.fixedTimestepMs > 0 ? this.accumulatorMs / this.fixedTimestepMs : 0
    callbacks.render(alpha)
  }
}
